import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Pagination,
  Button,
} from "@mui/material";
import { Book, Add } from "@mui/icons-material";
import { useNotification } from "@refinedev/core";
import {
  findPhrasesEditor,
  getBrandsWithGlobalPhrasesEditor,
  getPhraseUsage,
  updatePhrase,
  deletePhrase,
  createPhrase,
  EditorPhrase,
  EditorBrand,
} from "../../api/phrasebook_editor";
import { PhraseStatus } from "../../api/phrasebook";
import { LanguageCode } from "../../pages/playground/types";
import PhrasebookTable from "./phrasebook-table";
import SearchFilters from "./search-filters";
import PhraseEditDialog from "./phrase-edit-dialog";
import PhraseUsageDialog from "./phrase-usage-dialog";
import PhraseDeleteDialog from "./phrase-delete-dialog";
import ImportExportComponent from "./import-export-component";

// Use the EditorBrand type from the API
type Brand = EditorBrand;

const PhrasebookEditor: React.FC = () => {
  const { open: notify } = useNotification();
  const [phrases, setPhrases] = useState<EditorPhrase[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBrandIds, setSelectedBrandIds] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showUsageDialog, setShowUsageDialog] = useState(false);
  const [selectedPhraseForUsage, setSelectedPhraseForUsage] =
    useState<EditorPhrase | null>(null);
  const [selectedPhraseForEdit, setSelectedPhraseForEdit] =
    useState<EditorPhrase | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedPhraseForDelete, setSelectedPhraseForDelete] =
    useState<EditorPhrase | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [usageJobs, setUsageJobs] = useState<
    Array<{
      job_id: string;
      master_id: string;
      created_at: string;
      status: string;
      phrase_type: string;
      phrase_order: number;
    }>
  >([]);
  const [usageDialogPage, setUsageDialogPage] = useState(1);
  const [loadingUsage, setLoadingUsage] = useState(false);

  const usageJobsPerPage = 10;
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [languageFilter, setLanguageFilter] = useState<string>("");
  const [usedPhrasesOnly, setUsedPhrasesOnly] = useState<boolean>(false);
  const [suitabilityFilter, setSuitabilityFilter] = useState<string>("");

  // Create a map of brand IDs to names for the export dialog
  const brandNamesMap = useMemo(() => {
    return brands.reduce((acc, brand) => {
      acc[brand.id] = brand.name;
      return acc;
    }, {} as Record<string, string>);
  }, [brands]);

  const pageSize = 15;

  const loadBrands = useCallback(async () => {
    try {
      const response = await getBrandsWithGlobalPhrasesEditor();
      setBrands(response.data.data || []);
    } catch (err) {
      console.error("Error loading brands:", err);
      notify?.({
        message: "Failed to load brands",
        type: "error",
      });
      setBrands([]);
    }
  }, [notify]);

  const loadPhrases = useCallback(
    async (
      page: number = 1,
      query: string = "",
      brandIds: string[] = [],
      statusFilter: string = "",
      originalLanguageFilter: string = "",
      usedOnly: boolean = false,
      suitabilityFilter: string = ""
    ) => {
      setLoading(true);

      try {
        const brandIdsParam = brandIds.length > 0 ? brandIds : undefined;
        const response = await findPhrasesEditor({
          brandIds: brandIdsParam,
          page,
          limit: pageSize,
          query: query || undefined,
          statusFilter: statusFilter || undefined,
          originalLanguageFilter: originalLanguageFilter || undefined,
          usedPhrasesOnly: usedOnly,
          suitableForTitle: suitabilityFilter === "title" ? true : undefined,
          suitableForDescription: suitabilityFilter === "description" ? true : undefined,
          suitableForBulletPoints: suitabilityFilter === "bullet_points" ? true : undefined,
        });

        // Handle the proper paginated response structure
        const responseData = response.data.data;
        setPhrases(responseData.items || []);
        setTotalPages(responseData.pages || 1);
        setTotalCount(responseData.total || 0);
        setCurrentPage(page);
      } catch (err) {
        console.error("Error loading phrases:", err);
        notify?.({
          message: "Failed to load phrases",
          type: "error",
        });
        setPhrases([]);
        setTotalPages(1);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [notify]
  );

  const refreshPhrases = useCallback((page?: number) => {
    const targetPage = page || currentPage;
    if (page) {
      setCurrentPage(page);
    }
    loadPhrases(
      targetPage,
      searchQuery,
      selectedBrandIds,
      statusFilter,
      languageFilter,
      usedPhrasesOnly,
      suitabilityFilter
    );
  }, [
    loadPhrases,
    currentPage,
    searchQuery,
    selectedBrandIds,
    statusFilter,
    languageFilter,
    usedPhrasesOnly,
    suitabilityFilter,
  ]);

  const handleSearch = useCallback(() => {
    refreshPhrases(1); // Reset to first page on search
  }, [refreshPhrases]);

  const handlePageChange = useCallback(
    (_: React.ChangeEvent<unknown>, page: number) => {
      refreshPhrases(page);
    },
    [refreshPhrases]
  );

  const handleRefresh = useCallback(() => {
    refreshPhrases(); // Use current page
  }, [refreshPhrases]);

  const handleBrandFilter = useCallback(
    (brandId: string) => {
      const newBrandIds = selectedBrandIds.includes(brandId)
        ? selectedBrandIds.filter((id) => id !== brandId)
        : [...selectedBrandIds, brandId];
      setSelectedBrandIds(newBrandIds);
      refreshPhrases(1);
    },
    [selectedBrandIds, refreshPhrases]
  );

  const handleShowUsage = useCallback(async (phrase: EditorPhrase) => {
    setSelectedPhraseForUsage(phrase);
    setShowUsageDialog(true);
    setUsageDialogPage(1);
    setLoadingUsage(true);

    try {
      const response = await getPhraseUsage(phrase.id!);
      setUsageJobs(response.data.data || []);
    } catch (err) {
      console.error("Error loading phrase usage:", err);
      setUsageJobs([]);
    } finally {
      setLoadingUsage(false);
    }
  }, []);

  const handleCloseUsageDialog = useCallback(() => {
    setShowUsageDialog(false);
    setSelectedPhraseForUsage(null);
    setUsageJobs([]);
    setUsageDialogPage(1);
  }, []);

  const handleEditPhrase = useCallback((phrase: EditorPhrase) => {
    setSelectedPhraseForEdit(phrase);
    setShowEditDialog(true);
  }, []);

  const handleCloseEditDialog = useCallback(() => {
    setShowEditDialog(false);
    setSelectedPhraseForEdit(null);
  }, []);
  
  const handleSearchForPhrase = useCallback((phraseQuery: string) => {
    // Reset all filters to ensure clean search
    setSelectedBrandIds([]);
    setStatusFilter("");
    setLanguageFilter("");
    setUsedPhrasesOnly(false);
    setSuitabilityFilter("");
    
    // Set the search query and trigger search
    setSearchQuery(phraseQuery);
    // The auto-update effect will trigger the search automatically
  }, []);

  const handleSavePhrase = useCallback(
    async (phraseData: EditorPhrase) => {
      try {
        if (phraseData.id) {
          // Update existing phrase
          await updatePhrase(phraseData.id, {
            phrase: phraseData.phrase,
            status: phraseData.status?.toString(),
            suitable_for_title: phraseData.suitable_for_title,
            suitable_for_description: phraseData.suitable_for_description,
            suitable_for_bullet_points: phraseData.suitable_for_bullet_points,
            brand_ids: phraseData.brand_ids,
            translation_status: phraseData.translation_status,
            original_language: phraseData.original_language,
          });
          notify?.({
            message: "Phrase updated successfully",
            type: "success",
          });
        } else {
          // Create new phrase - original_language is required
          if (!phraseData.original_language) {
            throw new Error("Original language is required for new phrases");
          }
          
          await createPhrase({
            phrase: phraseData.phrase,
            status: phraseData.status?.toString(),
            suitable_for_title: phraseData.suitable_for_title,
            suitable_for_description: phraseData.suitable_for_description,
            suitable_for_bullet_points: phraseData.suitable_for_bullet_points,
            brand_ids: phraseData.brand_ids,
            translation_status: phraseData.translation_status,
            original_language: phraseData.original_language,
          });
          notify?.({
            message: "Phrase created successfully",
            type: "success",
          });
        }

        // Refresh the phrases list
        refreshPhrases();
      } catch (error: unknown) {
        console.error("Error saving phrase:", error);
        const errorMessage =
          (error as { response?: { data?: { message?: string } } })?.response
            ?.data?.message || "Failed to save phrase";
        notify?.({
          message: errorMessage,
          type: "error",
        });
      }
    },
    [refreshPhrases, notify]
  );

  const handleDeletePhrase = useCallback(
    (phrase: EditorPhrase) => {
      if (phrase.usage_count && phrase.usage_count > 0) {
        notify?.({
          message: `Cannot delete phrase - it is used in ${phrase.usage_count} jobs`,
          type: "error",
        });
        return;
      }
      setSelectedPhraseForDelete(phrase);
      setShowDeleteDialog(true);
    },
    [notify]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedPhraseForDelete?.id) return;

    try {
      await deletePhrase(selectedPhraseForDelete.id);
      setShowDeleteDialog(false);
      setSelectedPhraseForDelete(null);
      notify?.({
        message: "Phrase deleted successfully",
        type: "success",
      });

      // Refresh the phrases list
      refreshPhrases();
    } catch (error: unknown) {
      console.error("Error deleting phrase:", error);
      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || "Failed to delete phrase";
      notify?.({
        message: errorMessage,
        type: "error",
      });
      setShowDeleteDialog(false);
      setSelectedPhraseForDelete(null);
    }
  }, [selectedPhraseForDelete, refreshPhrases, notify]);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteDialog(false);
    setSelectedPhraseForDelete(null);
  }, []);

  useEffect(() => {
    loadBrands();
    loadPhrases();
  }, [loadBrands, loadPhrases]);

  // Auto-update phrases when filters change (not when page changes)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Reset to page 1 and load phrases when filters change
      setCurrentPage(1);
      loadPhrases(
        1, // Always go to page 1 when filters change
        searchQuery,
        selectedBrandIds,
        statusFilter,
        languageFilter,
        usedPhrasesOnly,
        suitabilityFilter
      );
    }, 300); // Debounce for 300ms

    return () => clearTimeout(timeoutId);
  }, [
    searchQuery,
    selectedBrandIds,
    statusFilter,
    languageFilter,
    usedPhrasesOnly,
    suitabilityFilter,
    loadPhrases, // Only depend on loadPhrases, not refreshPhrases
  ]);

  const handleAddPhrase = useCallback(() => {
    // Create an empty phrase object for adding new phrase
    // Initialize all translation statuses to false - they'll be set when original language is selected
    const newPhrase: EditorPhrase = {
      phrase: "",
      status: PhraseStatus.UNREVIEWED,
      suitable_for_title: false,
      suitable_for_description: false,
      suitable_for_bullet_points: false,
      brand_ids: [],
      translation_status: Object.values(LanguageCode).reduce(
        (acc, lang) => ({
          ...acc,
          [lang]: false,
        }),
        {} as Record<LanguageCode, boolean>
      ),
      usage_count: 0,
      original_language: undefined, // Will be required to be set by user
    };
    setSelectedPhraseForEdit(newPhrase);
    setShowEditDialog(true);
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">
          <Book sx={{ mr: 1, verticalAlign: "middle" }} />
          Phrasebook Editor
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddPhrase}
        >
          Add Phrase
        </Button>
      </Box>

      {/* Import/Export Component */}
      <ImportExportComponent
        searchQuery={searchQuery}
        selectedBrandIds={selectedBrandIds}
        statusFilter={statusFilter}
        languageFilter={languageFilter}
        usedPhrasesOnly={usedPhrasesOnly}
        suitabilityFilter={suitabilityFilter}
        totalPhrases={totalCount}
        brandNamesMap={brandNamesMap}
        onImportCompleted={() => {
          refreshPhrases();
          loadBrands();
        }}
      />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <SearchFilters
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            selectedBrandIds={selectedBrandIds}
            setSelectedBrandIds={setSelectedBrandIds}
            brands={brands}
            onSearch={handleSearch}
            onRefresh={handleRefresh}
            loading={loading}
            totalCount={totalCount}
            displayCount={phrases?.length || 0}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            languageFilter={languageFilter}
            setLanguageFilter={setLanguageFilter}
            usedPhrasesOnly={usedPhrasesOnly}
            setUsedPhrasesOnly={setUsedPhrasesOnly}
            suitabilityFilter={suitabilityFilter}
            setSuitabilityFilter={setSuitabilityFilter}
          />
        </CardContent>
      </Card>

      <PhrasebookTable
        phrases={phrases}
        loading={loading}
        brands={brands}
        onBrandFilter={handleBrandFilter}
        onShowUsage={handleShowUsage}
        onEditPhrase={handleEditPhrase}
        onDeletePhrase={handleDeletePhrase}
      />

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            disabled={loading}
          />
        </Box>
      )}

      {/* Usage Dialog */}
      <PhraseUsageDialog
        open={showUsageDialog}
        phrase={selectedPhraseForUsage}
        onClose={handleCloseUsageDialog}
        usageJobs={usageJobs}
        loading={loadingUsage}
        currentPage={usageDialogPage}
        onPageChange={setUsageDialogPage}
        jobsPerPage={usageJobsPerPage}
      />

      {/* Edit Dialog */}
      <PhraseEditDialog
        open={showEditDialog}
        phrase={selectedPhraseForEdit}
        brands={brands}
        onClose={handleCloseEditDialog}
        onSave={handleSavePhrase}
        onSearchForPhrase={handleSearchForPhrase}
      />

      {/* Delete Confirmation Dialog */}
      <PhraseDeleteDialog
        open={showDeleteDialog}
        phrase={selectedPhraseForDelete}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
};

export default PhrasebookEditor;
